// aiUsage / api — the AI-usage pages' reads: fetch, then parse the wire
// value with the shared contract (`@bilda/app/agentCallAdmin`) at the
// boundary. Impure (network); used only by the page's host.

export { aiUsageApi, aiUsageQueryString, csvHref };

import {
    AI_USAGE_PAGE_SIZE,
    type AiUsageCallDetail,
    type AiUsageCallList,
    type AiUsageRates,
    type AiUsageSummary,
    aiUsageCallDetailDto,
    aiUsageCallListDto,
    aiUsageRatesDto,
    aiUsageSummaryDto,
} from "@bilda/app/agentCallAdmin";
import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "../externalError.js";
import { API_BASE, getAdmin } from "../httpClient.js";
import type { PeriodRange } from "./period.js";
import { type AiUsageRoute, FILTER_KEYS } from "./route.js";

type Read<T> = Promise<Result.Result<T, ExternalError>>;

// The one thing this module needs from a contract schema.
type Parser<T> = {
    readonly safeParse: (
        v: unknown,
    ) =>
        | { readonly success: true; readonly data: T }
        | { readonly success: false };
};

type AiUsageApi = {
    readonly summary: (query: string) => Read<AiUsageSummary>;
    readonly calls: (query: string) => Read<AiUsageCallList>;
    readonly call: (callId: string) => Read<AiUsageCallDetail>;
    readonly rates: (query: string) => Read<AiUsageRates>;
};

// The server query for a route over a concrete period.
const aiUsageQueryString = (
    route: AiUsageRoute,
    range: PeriodRange,
    tz: string,
): string => {
    const p = new URLSearchParams({
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        tz,
    });
    for (const key of FILTER_KEYS) {
        const v = route.filters[key];
        if (v !== undefined) p.set(key, v);
    }
    p.set("sort", route.sort);
    p.set("dir", route.dir);
    p.set("offset", String(route.page * AI_USAGE_PAGE_SIZE));
    p.set("by", route.by);
    return p.toString();
};

const csvHref = (query: string): string =>
    `${API_BASE}/admin/ai-usage/calls.csv?${query}`;

const parsed = async <T>(
    read: Promise<Result.Result<unknown, ExternalError>>,
    schema: Parser<T>,
): Read<T> => {
    const r = await read;
    if (Result.isErr(r)) return r;
    const p = schema.safeParse(r.value);
    return p.success
        ? Result.ok(p.data)
        : Result.err(mkMalformed("AI usage response did not match"));
};

const aiUsageApi: AiUsageApi = {
    summary: (q) =>
        parsed(getAdmin(`ai-usage/summary?${q}`), aiUsageSummaryDto),
    calls: (q) => parsed(getAdmin(`ai-usage/calls?${q}`), aiUsageCallListDto),
    call: (id) =>
        parsed(
            getAdmin(`ai-usage/calls/${encodeURIComponent(id)}`),
            aiUsageCallDetailDto,
        ),
    rates: (q) => parsed(getAdmin(`ai-usage/rates?${q}`), aiUsageRatesDto),
};
